import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CountrySelectMenu } from 'ui/CountrySelectMenu';
import { ETableStructureType } from 'ui/EasTable/types';
import {
	listSalesChannelsCatalogRequest,
	getStoreSettingsRequest,
	getStoresByClientIdRequest,
	resetUpdateStoreSettingsSuccess,
	updateStoreSettingsRequest,
} from 'redux/reducers/shopifyStores/reducer';
import {
	selectSalesChannelsCatalogData,
	selectSalesChannelsCatalogLoading,
	selectStoreSettings,
	selectStoreSettingsLoading,
	selectUpdateStoreSettingsLoading,
	selectUpdateStoreSettingsSuccess,
} from 'redux/reducers/shopifyStores/selectors';
import { TShopifySalesChannel } from 'redux/reducers/shopifyStores/types';
import { getEmByIdFull } from 'redux/reducers/emManagement/selectors';
import {
	SALES_CHANNELS_HINT,
	TEditShopifyStoreDialogProps,
	TEditShopifyStoreFormValues,
	TYPE_OF_GOODS_OPTIONS,
	WEIGHT_UNIT_OPTIONS,
} from './types';
import { buildUpdateConfig, getEmptyInitialValues, getInitialValuesFromSettings } from './utils';
import { EasTableValue } from '../../../../../../ui/EasTable/EasTableRow/EasTableValue';
import { Notification } from '../../../../../../ui/Notification';

const validationSchema = Yup.object({
	export_period: Yup.number()
		.typeError('Must be a number')
		.min(0, 'Must be greater than or equal to 0')
		.required('Required'),
	type_of_goods: Yup.mixed<'tbe' | 'goods'>().oneOf(['tbe', 'goods']).required('Required'),
	seller_registration_country: Yup.string().length(2, 'ISO2 code').required('Required'),
	originating_country: Yup.string().length(2, 'ISO2 code').required('Required'),
	location_warehouse_country: Yup.string().length(2, 'ISO2 code').required('Required'),
});

const HeaderRow = ({
	label,
	value,
	borderBottom = true,
	type = ETableStructureType.STRING,
}: {
	label: string;
	value: string | number | null;
	borderBottom?: boolean;
	type?: ETableStructureType;
}) => (
	<Box
		sx={{
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'flex-start',
			gap: 1,
			py: 0.5,
			borderBottom: borderBottom ? '1px solid' : 'none',
			borderColor: 'divider',
		}}
	>
		<Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
			{label}
		</Typography>
		<EasTableValue type={type} value={value} />
	</Box>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
	<Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mt: 1.5, mb: 0.5 }}>
		{children}
	</Typography>
);

export const EditShopifyStoreDialog = ({
	open,
	storeId,
	onClose,
}: TEditShopifyStoreDialogProps) => {
	const dispatch = useDispatch();
	const settings = useSelector(selectStoreSettings);
	const settingsLoading = useSelector(selectStoreSettingsLoading);
	const catalogChannels = useSelector(selectSalesChannelsCatalogData);
	const catalogLoading = useSelector(selectSalesChannelsCatalogLoading);
	const updateLoading = useSelector(selectUpdateStoreSettingsLoading);
	const updateSuccess = useSelector(selectUpdateStoreSettingsSuccess);
	const emDataFull = useSelector(getEmByIdFull);

	useEffect(() => {
		if (open && storeId != null) {
			dispatch(getStoreSettingsRequest({ store_id: storeId }));
			dispatch(
				listSalesChannelsCatalogRequest({
					pageSize: 100,
					pageNumber: 1,
					searchString: '',
					filters: [{ fieldName: 'isActive', value: true, operator: '=' }],
				}),
			);
		}
		// Note: do not clear storeSettings on close — the host page (e.g.
		// ShopifyStoreDetails) may still be displaying the data.
	}, [open, storeId, dispatch]);

	const formik = useFormik<TEditShopifyStoreFormValues>({
		enableReinitialize: true,
		initialValues: settings ? getInitialValuesFromSettings(settings) : getEmptyInitialValues(),
		validationSchema,
		onSubmit: (values) => {
			if (storeId == null) return;
			const config = buildUpdateConfig(settings, values);
			dispatch(
				updateStoreSettingsRequest({
					store_id: storeId,
					...config,
				}),
			);
		},
	});

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	const handleCloseRef = useRef(handleClose);
	useEffect(() => {
		handleCloseRef.current = handleClose;
	});

	const prevUpdateLoadingRef = useRef(updateLoading);
	useEffect(() => {
		const wasLoading = prevUpdateLoadingRef.current;
		prevUpdateLoadingRef.current = updateLoading;
		if (open && wasLoading && !updateLoading && updateSuccess) {
			dispatch(resetUpdateStoreSettingsSuccess());
			if (storeId != null) {
				dispatch(getStoreSettingsRequest({ store_id: storeId }));
			}
			if (emDataFull?.clientId) {
				dispatch(getStoresByClientIdRequest({ client_id: String(emDataFull.clientId) }));
			}
			handleCloseRef.current();
		}
	}, [open, updateLoading, updateSuccess, dispatch, emDataFull?.clientId, storeId]);

	const channelOptions: TShopifySalesChannel[] = useMemo(
		() => catalogChannels ?? [],
		[catalogChannels],
	);

	const selectedChannels = useMemo(() => {
		const selectedIds = formik.values.sales_channels_selected_will_be_processed;
		const known = channelOptions.filter((channel) => selectedIds.includes(channel.app_id));
		const knownIds = new Set(known.map((c) => c.app_id));
		// Preserve selected app_ids that are not present in the catalog (e.g. legacy)
		const unknownPlaceholders: TShopifySalesChannel[] = selectedIds
			.filter((id) => !knownIds.has(id))
			.map((id) => ({
				id,
				app_id: id,
				title: String(id),
				shop_url: null,
				comment: null,
				is_new: false,
				is_active: false,
				created_at: '',
				updated_at: '',
			}));
		return [...known, ...unknownPlaceholders];
	}, [channelOptions, formik.values.sales_channels_selected_will_be_processed]);

	const store = settings?.store;
	const generalAny = (settings?.config?.general ?? {}) as Record<string, unknown> & {
		enabled_at?: string | null;
		export?: { updated_at?: string | null };
	};

	const isLoadingInitial = settingsLoading && !settings;
	const disabled = updateLoading || isLoadingInitial;

	return (
		<Dialog
			open={open}
			onClose={updateLoading ? () => {} : handleClose}
			fullWidth
			maxWidth="lg"
			PaperProps={{ sx: { borderRadius: '12px' } }}
		>
			<DialogTitle color="primary">
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<span>Edit Shopify Store</span>
					{(settingsLoading || catalogLoading) && <CircularProgress size={20} />}
				</Box>
			</DialogTitle>
			<form onSubmit={formik.handleSubmit}>
				<DialogContent sx={{ p: '0px 20px 20px 20px' }}>
					{isLoadingInitial ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
							<CircularProgress />
						</Box>
					) : (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
							{/* Header (read-only) */}
							<SectionTitle>Store Details</SectionTitle>
							<Box
								sx={{
									border: '1px solid',
									borderColor: 'divider',
									borderRadius: '12px',
									p: 1.5,
									display: 'flex',
									justifyContent: 'row',
									alignItems: 'center',
									gap: 1.5,
									width: '100%',
								}}
							>
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
									<HeaderRow
										label="Store ID"
										value={store?.store_id ?? null}
										type={ETableStructureType.NUMBER}
									/>
									<HeaderRow
										label="Store URL"
										value={store?.store_url ?? null}
										type={ETableStructureType.COPYABLE_TEXT}
									/>
									<HeaderRow
										label="Storefront URL"
										value={store?.store_front_url ?? null}
										type={ETableStructureType.COPYABLE_TEXT}
									/>
									<HeaderRow
										label="Name"
										value={store?.name ?? null}
										borderBottom={false}
										type={ETableStructureType.STRING}
									/>
								</Box>
								<Divider orientation="vertical" flexItem />
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
									<HeaderRow
										label="Blocked At"
										value={store?.blocked_at ?? null}
										type={ETableStructureType.DATE_TIME}
									/>
									<HeaderRow
										label="Disabled At"
										value={store?.disabled_at ?? null}
										type={ETableStructureType.DATE_TIME}
									/>
									<HeaderRow
										label="Application Enabled At"
										value={(generalAny.enabled_at as string | null | undefined) ?? null}
										type={ETableStructureType.DATE_TIME}
									/>
									<HeaderRow
										label="Last Order Synchronisation At"
										value={(generalAny.export?.updated_at as string | null | undefined) ?? null}
										type={ETableStructureType.DATE_TIME}
										borderBottom={false}
									/>
								</Box>
							</Box>

							{/* Application */}
							<SectionTitle>Application Status</SectionTitle>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
								<FormControlLabel
									control={
										<Checkbox
											name="enabled"
											checked={formik.values.enabled}
											onChange={formik.handleChange}
											disabled={disabled}
										/>
									}
									label="Application enabled"
								/>
								<TextField
									name="export_period"
									label="Order Export Period, minutes"
									size="small"
									type="number"
									inputProps={{ min: 0, step: 1 }}
									value={formik.values.export_period}
									onChange={(e) => {
										const raw = e.target.value;
										void formik.setFieldValue('export_period', raw === '' ? 0 : Number(raw));
									}}
									onBlur={formik.handleBlur}
									error={formik.touched.export_period && Boolean(formik.errors.export_period)}
									helperText={formik.touched.export_period && formik.errors.export_period}
									disabled={disabled}
								/>
							</Box>

							{/* EAS Params */}
							<SectionTitle>Application Parameters</SectionTitle>
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
									gap: 1.5,
								}}
							>
								<FormControlLabel
									control={
										<Checkbox
											name="immediately_process_paid_orders"
											checked={formik.values.immediately_process_paid_orders}
											onChange={formik.handleChange}
											disabled={disabled}
										/>
									}
									label="Immediately process paid orders"
								/>
								<FormControl size="small">
									<InputLabel id="weight-unit-label">Weight units</InputLabel>
									<Select
										labelId="weight-unit-label"
										label="Weight units"
										name="weight_unit"
										value={formik.values.weight_unit}
										onChange={formik.handleChange}
										disabled={disabled}
									>
										{WEIGHT_UNIT_OPTIONS.map((opt) => (
											<MenuItem key={opt.value} value={opt.value}>
												{opt.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>

								<FormControlLabel
									control={
										<Checkbox
											name="act_as_disclosed_agent"
											checked={formik.values.act_as_disclosed_agent}
											onChange={formik.handleChange}
											disabled={disabled}
										/>
									}
									label="Act as disclose agent"
								/>
								<TextField
									name="hs6p_received"
									label="Default HS code"
									size="small"
									value={formik.values.hs6p_received}
									onChange={formik.handleChange}
									disabled={disabled}
								/>

								<FormControl size="small">
									<InputLabel id="type-of-goods-label">Default product type</InputLabel>
									<Select
										labelId="type-of-goods-label"
										label="Default product type"
										name="type_of_goods"
										value={formik.values.type_of_goods}
										onChange={formik.handleChange}
										disabled={disabled}
									>
										{TYPE_OF_GOODS_OPTIONS.map((opt) => (
											<MenuItem key={opt.value} value={opt.value}>
												{opt.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControlLabel
									control={
										<Checkbox
											name="reduced_tbe_vat_group"
											checked={formik.values.reduced_tbe_vat_group}
											onChange={formik.handleChange}
											disabled={disabled}
										/>
									}
									label="Default Reduced tbe vat group"
								/>

								<Box
									sx={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
								>
									<CountrySelectMenu
										label="Default Seller registration country"
										type={ETableStructureType.COUNTRY_CODE}
										countryCode={formik.values.seller_registration_country}
										onChange={(code) => {
											void formik.setFieldValue('seller_registration_country', code);
										}}
										size="small"
									/>
								</Box>
								<Box
									sx={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
								>
									<CountrySelectMenu
										label="Default Origin Country"
										type={ETableStructureType.COUNTRY_CODE}
										countryCode={formik.values.originating_country}
										onChange={(code) => {
											void formik.setFieldValue('originating_country', code);
										}}
										size="small"
									/>
								</Box>
								<Box
									sx={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
								>
									<CountrySelectMenu
										label="Location Warehouse Country"
										type={ETableStructureType.COUNTRY_CODE}
										countryCode={formik.values.location_warehouse_country}
										onChange={(code) => {
											void formik.setFieldValue('location_warehouse_country', code);
										}}
										size="small"
									/>
								</Box>

								<FormControlLabel
									control={
										<Checkbox
											name="order_tags_enable"
											checked={formik.values.order_tags_enable}
											onChange={formik.handleChange}
											disabled={disabled}
										/>
									}
									label="Add EAS tags to the order"
								/>
								<FormControlLabel
									control={
										<Checkbox
											name="handle_ireland_as_ioss"
											checked={formik.values.handle_ireland_as_ioss}
											onChange={formik.handleChange}
											disabled={disabled}
										/>
									}
									label="Handle Northern Ireland as IOSS/OSS"
								/>
								<FormControlLabel
									sx={{ gridColumn: { sm: 'span 2' } }}
									control={
										<Checkbox
											name="swisspost_full_ddp_delivery_customs_description"
											checked={formik.values.swisspost_full_ddp_delivery_customs_description}
											disabled
										/>
									}
									label="Add customs description meta to the products (Swiss post customers)"
								/>
							</Box>

							{/* Sales Channels */}
							<Divider sx={{ my: 1 }} />
							<SectionTitle>Sales Channels Configuration</SectionTitle>
							<Alert severity="warning" sx={{ color: 'warning.dark' }}>
								When sales channel override enabled, then orders from selected channel will be
								processed. By default orders from most of sales channels are skipped.
							</Alert>
							<FormControlLabel
								control={
									<Checkbox
										name="sales_channels_enabled"
										checked={formik.values.sales_channels_enabled}
										onChange={formik.handleChange}
										disabled={disabled}
									/>
								}
								label="Override skipped sales channels logic"
							/>
							<Autocomplete
								multiple
								options={channelOptions}
								loading={catalogLoading}
								disabled={disabled}
								value={selectedChannels}
								getOptionLabel={(option) => `${option.title}`}
								isOptionEqualToValue={(option, value) => option.app_id === value.app_id}
								onChange={(_event, options) => {
									void formik.setFieldValue(
										'sales_channels_selected_will_be_processed',
										options.map((o) => o.app_id),
									);
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										size="small"
										label="Process orders for selected channels"
										InputProps={{
											...params.InputProps,
											endAdornment: (
												<>
													{catalogLoading ? (
														<CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
													) : null}
													{/* <Tooltip title={SALES_CHANNELS_HINT} arrow placement="top">
														<InfoOutlinedIcon
															color="action"
															fontSize="small"
															sx={{ mr: 1, cursor: 'help' }}
														/>
													</Tooltip> */}
													{params.InputProps.endAdornment}
												</>
											),
										}}
									/>
								)}
							/>
							{/* <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
								{SALES_CHANNELS_HINT}
							</Typography> */}
						</Box>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={handleClose} disabled={updateLoading}>
						Cancel
					</Button>
					<Button
						type="submit"
						variant="contained"
						color="primary"
						disabled={updateLoading || isLoadingInitial}
					>
						{updateLoading ? <CircularProgress size={22} color="inherit" /> : 'Save'}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
